import React, { useState } from 'react';
import { DuplicateUploadNotice } from '../hooks/useProjectFileDrop';
import { fetchWithActivity } from '../utils/fetchWithActivity';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface DuplicateUploadBannerProps {
  notice: DuplicateUploadNotice;
  token: string | null;
  onDismiss: () => void;
  /** Called after the uploaded copy is successfully removed. */
  onRemoved: () => void;
  /** Omitted when there's no document list in view to scroll to (e.g. inside a modal). */
  onViewExisting?: () => void;
  /** Override the default `mb-4` spacing — e.g. `mb-0` when the caller positions the banner itself. */
  className?: string;
}

// Persistent (until dismissed) duplicate-upload notice with three actions:
// View existing / Remove this copy / Keep both (E8). Shared by the Documents
// tab, ProjectCard, and the in-modal workflow uploader.
const DuplicateUploadBanner: React.FC<DuplicateUploadBannerProps> = ({ notice, token, onDismiss, onRemoved, onViewExisting, className = 'mb-4' }) => {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/documents/${notice.uploadedDocumentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok || res.status === 204) {
        onRemoved();
      }
    } finally {
      setRemoving(false);
      setConfirmingRemove(false);
    }
  };

  return (
    <div className={`${className} px-3 py-2 bg-amber-500 text-white text-xs rounded-lg flex items-center justify-between gap-3 flex-wrap`}>
      <span className="min-w-0">{notice.message}</span>
      <div className="flex items-center gap-3 flex-shrink-0">
        {confirmingRemove ? (
          <>
            <span>Remove {notice.fileName}?</span>
            <button onClick={handleRemove} disabled={removing} className="underline font-medium disabled:opacity-50">
              {removing ? 'Removing…' : 'Yes, remove'}
            </button>
            <button onClick={() => setConfirmingRemove(false)} className="underline font-medium">
              Cancel
            </button>
          </>
        ) : (
          <>
            {onViewExisting && (
              <button onClick={onViewExisting} className="underline font-medium">
                View existing
              </button>
            )}
            <button onClick={() => setConfirmingRemove(true)} className="underline font-medium">
              Remove this copy
            </button>
            <button onClick={onDismiss} className="underline font-medium">
              Keep both
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DuplicateUploadBanner;
